import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, orderBy, deleteDoc, addDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { WhatsAppMessageType, WhatsAppMessageData } from './hybridWhatsAppService';
import { hybridWhatsAppService } from './hybridWhatsAppService';

// واجهة الرسالة المجدولة
export interface ScheduledMessage {
  id: string;
  messageType: WhatsAppMessageType;
  messageData: WhatsAppMessageData;
  scheduledAt: Date;
  status: 'pending' | 'sent' | 'failed' | 'cancelled';
  createdAt: Date;
  sentAt?: Date;
  error?: string;
  repeatType?: 'none' | 'daily' | 'weekly' | 'monthly';
  repeatUntil?: Date;
  lastSent?: Date;
}

class WhatsAppSchedulerService {
  private readonly COLLECTION_NAME = 'whatsapp_scheduled_messages';

  // جدولة رسالة جديدة
  public async scheduleMessage(
    messageType: WhatsAppMessageType,
    messageData: WhatsAppMessageData,
    scheduledAt: Date,
    repeatType: 'none' | 'daily' | 'weekly' | 'monthly' = 'none',
    repeatUntil?: Date
  ): Promise<string> {
    try {
      const scheduledMessage: Omit<ScheduledMessage, 'id'> = {
        messageType,
        messageData,
        scheduledAt,
        status: 'pending',
        createdAt: new Date(),
        repeatType,
        repeatUntil
      };

      const docRef = await addDoc(collection(db, this.COLLECTION_NAME), scheduledMessage);
      return docRef.id;
    } catch (error) {
      console.error('Error scheduling WhatsApp message:', error);
      throw error;
    }
  }

  // الحصول على الرسائل المجدولة المعلقة
  public async getPendingMessages(): Promise<ScheduledMessage[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('status', '==', 'pending'),
        where('scheduledAt', '<=', new Date()),
        orderBy('scheduledAt', 'asc')
      );

      const querySnapshot = await getDocs(q);
      const messages: ScheduledMessage[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        messages.push({
          id: doc.id,
          ...data,
          scheduledAt: data.scheduledAt?.toDate() || new Date(),
          createdAt: data.createdAt?.toDate() || new Date(),
          sentAt: data.sentAt?.toDate(),
          repeatUntil: data.repeatUntil?.toDate(),
          lastSent: data.lastSent?.toDate()
        } as ScheduledMessage);
      });

      return messages;
    } catch (error) {
      console.error('Error getting pending WhatsApp messages:', error);
      return [];
    }
  }

  // الحصول على جميع الرسائل المجدولة
  public async getAllScheduledMessages(): Promise<ScheduledMessage[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        orderBy('scheduledAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const messages: ScheduledMessage[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        messages.push({
          id: doc.id,
          ...data,
          scheduledAt: data.scheduledAt?.toDate() || new Date(),
          createdAt: data.createdAt?.toDate() || new Date(),
          sentAt: data.sentAt?.toDate(),
          repeatUntil: data.repeatUntil?.toDate(),
          lastSent: data.lastSent?.toDate()
        } as ScheduledMessage);
      });

      return messages;
    } catch (error) {
      console.error('Error getting all scheduled WhatsApp messages:', error);
      return [];
    }
  }

  // إرسال رسالة مجدولة
  public async sendScheduledMessage(messageId: string): Promise<boolean> {
    try {
      const messageRef = doc(db, this.COLLECTION_NAME, messageId);
      const messageSnap = await getDoc(messageRef);

      if (!messageSnap.exists()) {
        console.error('Scheduled message not found:', messageId);
        return false;
      }

      const message = messageSnap.data() as ScheduledMessage;

      // إرسال الرسالة
      const success = await hybridWhatsAppService.sendMessage(
        message.messageType,
        message.messageData
      );

      // تحديث حالة الرسالة
      const updateData: Partial<ScheduledMessage> = {
        status: success ? 'sent' : 'failed',
        sentAt: new Date(),
        lastSent: new Date()
      };

      if (!success) {
        updateData.error = 'فشل في إرسال الرسالة';
      }

      await updateDoc(messageRef, updateData);

      // إذا كانت الرسالة متكررة وتم إرسالها بنجاح، جدول الرسالة التالية
      if (success && message.repeatType !== 'none') {
        await this.scheduleNextRepeat(message);
      }

      return success;
    } catch (error) {
      console.error('Error sending scheduled WhatsApp message:', error);
      
      // تحديث حالة الرسالة إلى فشل
      try {
        const messageRef = doc(db, this.COLLECTION_NAME, messageId);
        await updateDoc(messageRef, {
          status: 'failed',
          error: 'خطأ في النظام أثناء الإرسال'
        });
      } catch (updateError) {
        console.error('Error updating failed message status:', updateError);
      }

      return false;
    }
  }

  // جدولة الرسالة المتكررة التالية
  private async scheduleNextRepeat(message: ScheduledMessage): Promise<void> {
    try {
      if (!message.repeatType || message.repeatType === 'none') {
        return;
      }

      const nextDate = new Date(message.scheduledAt);

      switch (message.repeatType) {
        case 'daily':
          nextDate.setDate(nextDate.getDate() + 1);
          break;
        case 'weekly':
          nextDate.setDate(nextDate.getDate() + 7);
          break;
        case 'monthly':
          nextDate.setMonth(nextDate.getMonth() + 1);
          break;
      }

      // التحقق من انتهاء فترة التكرار
      if (message.repeatUntil && nextDate > message.repeatUntil) {
        return;
      }

      // إنشاء رسالة مجدولة جديدة
      await this.scheduleMessage(
        message.messageType,
        message.messageData,
        nextDate,
        message.repeatType,
        message.repeatUntil
      );
    } catch (error) {
      console.error('Error scheduling next repeat:', error);
    }
  }

  // إلغاء رسالة مجدولة
  public async cancelScheduledMessage(messageId: string): Promise<boolean> {
    try {
      const messageRef = doc(db, this.COLLECTION_NAME, messageId);
      await updateDoc(messageRef, {
        status: 'cancelled'
      });
      return true;
    } catch (error) {
      console.error('Error cancelling scheduled WhatsApp message:', error);
      return false;
    }
  }

  // حذف رسالة مجدولة
  public async deleteScheduledMessage(messageId: string): Promise<boolean> {
    try {
      const messageRef = doc(db, this.COLLECTION_NAME, messageId);
      await deleteDoc(messageRef);
      return true;
    } catch (error) {
      console.error('Error deleting scheduled WhatsApp message:', error);
      return false;
    }
  }

  // معالجة الرسائل المعلقة (يتم استدعاؤها دورياً)
  public async processPendingMessages(): Promise<void> {
    try {
      const pendingMessages = await this.getPendingMessages();
      
      for (const message of pendingMessages) {
        await this.sendScheduledMessage(message.id);
        
        // انتظار قصير بين الرسائل لتجنب الإرسال المتزامن
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    } catch (error) {
      console.error('Error processing pending WhatsApp messages:', error);
    }
  }

  // تنظيف الرسائل القديمة (يتم استدعاؤها دورياً)
  public async cleanupOldMessages(daysOld: number = 30): Promise<void> {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysOld);

      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('createdAt', '<', cutoffDate),
        where('status', 'in', ['sent', 'failed', 'cancelled'])
      );

      const querySnapshot = await getDocs(q);
      
      const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);

      console.log(`Cleaned up ${querySnapshot.size} old WhatsApp messages`);
    } catch (error) {
      console.error('Error cleaning up old WhatsApp messages:', error);
    }
  }
}

// إنشاء مثيل واحد من الخدمة
export const whatsappSchedulerService = new WhatsAppSchedulerService();
export default whatsappSchedulerService;
